import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updateEmployeeGoal } from '../../../shared/api/employee.service';
import { type CountryCode } from '../../../shared/config';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  GOAL_AMOUNT_MAX,
  GOAL_AMOUNT_MIN,
  parseAmount,
  resolveCurrencyPrefix,
  resolveEmployeeSessionCountryCode,
  resolveThousandsSeparator,
} from '../../../shared/model';

type ValueIntroForm = {
  canSubmit: boolean;
  countryCode: CountryCode;
  currencyPrefix: string;
  goalValueAmount: number | null;
  goalValueInput: string;
  isSubmitting: boolean;
  setGoalValue: (nextUnmaskedValue: string) => void;
  skip: () => Promise<boolean>;
  submit: () => Promise<boolean>;
  submitError: string | null;
  thousandsSeparator: string;
};

type UseValueIntroFormParams = {
  countryCode?: CountryCode;
  initialGoalText?: string;
  initialGoalValueAmount?: number | null;
};

const EMPLOYEE_GOAL_QUERY_KEY = ['employee', 'goal'] as const;
export function useValueIntroForm({
  countryCode: providedCountryCode,
  initialGoalText: providedGoalText,
  initialGoalValueAmount: providedGoalValueAmount,
}: UseValueIntroFormParams = {}): ValueIntroForm {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { authContact } = useEmployeeSession();
  const goalSave = useMutation({
    mutationFn: updateEmployeeGoal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_GOAL_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const countryCode =
    providedCountryCode ?? resolveEmployeeSessionCountryCode(null, authContact);
  const initialGoalText = providedGoalText ?? '';
  const initialGoalValueAmount = providedGoalValueAmount ?? null;
  const [goalValueAmount, setGoalValueAmount] = useState(
    () => initialGoalValueAmount,
  );
  const goalValueInput =
    goalValueAmount === null ? '' : String(goalValueAmount);
  const currencyPrefix = resolveCurrencyPrefix(countryCode);
  const thousandsSeparator = resolveThousandsSeparator(countryCode);
  const canSubmit =
    goalValueAmount !== null &&
    goalValueAmount >= GOAL_AMOUNT_MIN &&
    goalValueAmount <= GOAL_AMOUNT_MAX;

  const setGoalValue = (nextUnmaskedValue: string) => {
    if (goalSave.isError) {
      goalSave.reset();
    }

    setGoalValueAmount(parseAmount(nextUnmaskedValue));
  };

  const persistGoalValue = async (nextTargetAmount: number) => {
    if (goalSave.isPending) {
      return false;
    }

    try {
      await goalSave.mutateAsync({
        targetAmount: nextTargetAmount,
        text: initialGoalText,
      });

      return true;
    } catch {
      return false;
    }
  };

  return {
    canSubmit,
    countryCode,
    currencyPrefix,
    goalValueAmount,
    goalValueInput,
    isSubmitting: goalSave.isPending,
    setGoalValue,
    skip: () => persistGoalValue(0),
    submit: () => persistGoalValue(goalValueAmount ?? 0),
    submitError: goalSave.isError ? t('login.networkError') : null,
    thousandsSeparator,
  };
}
